import { CustomerRequests } from '~/api/Customer';

import { decodeJwtExp } from './decodeJwtExp';

/**
 * Treat the token as expired if it has less than this many milliseconds left.
 * Covers clock skew and the time between "we checked" and "Intercom validates".
 */
const EXPIRATION_BUFFER_MS = 60 * 1000;

/**
 * Service responsible for obtaining and caching the Intercom Identity
 * Verification JWT. Wraps `CustomerRequests.requestIntercomIdentityVerification`
 * with:
 *  - expiration tracking (decoded from the JWT `exp` claim),
 *  - lazy refresh with a safety buffer,
 *  - in-flight request deduplication.
 *
 * The service is decoupled from React — it can be used from stores, reactions,
 * or hooks alike, and is safe for SSR (no `window` access at construction).
 */
class IntercomAuthService {
  private token: string | null = null;
  private expiresAt: number | null = null;
  private inFlight: Promise<string | null> | null = null;

  /**
   * Returns the cached token without any network access. Callers that cannot
   * `await` should prefer `getFreshToken` — cached token may be expired.
   */
  getCachedToken(): string | null {
    return this.token;
  }

  /**
   * Returns a non-expired token, fetching a new one if the cache is empty or
   * the current token is within the expiration buffer. Resolves to `null` if
   * the refresh request fails — callers should fall back to unauthenticated
   * behavior in that case.
   */
  getFreshToken(): Promise<string | null> {
    if (this.token && this.expiresAt && !this.isExpiring(this.expiresAt)) {
      return Promise.resolve(this.token);
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.fetchToken().finally(() => {
      this.inFlight = null;
    });

    return this.inFlight;
  }

  /**
   * Clear cached token. Call on logout so the next login fetches a fresh one.
   */
  invalidate(): void {
    this.token = null;
    this.expiresAt = null;
  }

  private isExpiring(expiresAt: number): boolean {
    return expiresAt - Date.now() < EXPIRATION_BUFFER_MS;
  }

  private async fetchToken(): Promise<string | null> {
    try {
      const response =
        await CustomerRequests.requestIntercomIdentityVerification();
      const token = response.data.intercomToken;
      const expiresAt = decodeJwtExp(token);

      this.token = token;
      this.expiresAt = expiresAt;

      return token;
    } catch {
      this.token = null;
      this.expiresAt = null;
      return null;
    }
  }
}

export const intercomAuth = new IntercomAuthService();
